import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { FileText, ArrowCounterClockwise } from "@phosphor-icons/react";

export function LetterTemplates() {
  const qc = useQueryClient();
  const [svc, setSvc] = useState("1040 Individual");
  const [text, setText] = useState("");
  const { data } = useQuery({ queryKey: ["letter-templates"], queryFn: () => api.get("/letter-templates").then((r) => r.data) });
  const current = (data?.templates || []).find((t) => t.service_type === svc);

  useEffect(() => { if (current) setText(current.paragraphs.join("\n\n")); }, [svc, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => api.put(`/letter-templates/${encodeURIComponent(svc)}`, { paragraphs: text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean) }),
    onSuccess: () => { toast.success(`Engagement letter wording saved for ${svc}`); qc.invalidateQueries({ queryKey: ["letter-templates"] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const reset = useMutation({
    mutationFn: () => api.delete(`/letter-templates/${encodeURIComponent(svc)}`),
    onSuccess: () => { toast.success("Reset to the firm default wording"); qc.invalidateQueries({ queryKey: ["letter-templates"] }); },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="letter-templates-card">
      <p className="text-sm font-medium text-zinc-900 flex items-center gap-1.5 mb-1"><FileText size={15} /> Engagement letter templates</p>
      <p className="text-xs text-zinc-500 mb-3">Edit the wording clients see per service type. Separate paragraphs with a blank line. Placeholders: {(data?.placeholders || []).join(" ")}</p>
      <div className="flex items-center gap-2 mb-2">
        <Select value={svc} onValueChange={setSvc}>
          <SelectTrigger data-testid="template-service-select" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>{(data?.templates || []).map((t) => <SelectItem key={t.service_type} value={t.service_type}>{t.service_type}{t.is_custom ? " · custom" : ""}</SelectItem>)}</SelectContent>
        </Select>
        {current?.is_custom && <span className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-sm px-1.5 py-0.5">CUSTOM — by {current.updated_by}</span>}
      </div>
      <Textarea data-testid="template-textarea" rows={9} value={text} onChange={(e) => setText(e.target.value)} className="text-xs font-mono mb-2" />
      <div className="flex gap-2">
        <Button data-testid="template-save-btn" size="sm" className="h-8 text-xs" disabled={!text.trim() || save.isPending} onClick={() => save.mutate()}>Save wording</Button>
        {current?.is_custom && (
          <Button data-testid="template-reset-btn" size="sm" variant="outline" className="h-8 text-xs" disabled={reset.isPending} onClick={() => reset.mutate()}>
            <ArrowCounterClockwise size={13} className="mr-1" /> Reset to default
          </Button>
        )}
      </div>
    </div>
  );
}
